
import { Collection, deserialize } from '@shared/jsona'
import VueLottiePlayer from 'vue-lottie-player'
import { Component, Mixins } from 'vue-property-decorator'
import Icon from '~/components/common/icon/Icon.vue'
import ReadMore from '~/components/common/typography/ReadMore.vue'
import VideoPlayer from '~/components/common/VideoPlayer.vue'
import WinnerBlastFeature from '~/components/features/WinnerBlast'
import { formatToUSDCurrency } from '~/lib/utils/format'
import { DEBUG_MODE } from '~/plugins/feature-flags/flags'
import {
  WINNER_BLAST_DISMISSED,
  WINNER_BLAST_REACTED,
  WINNER_BLAST_SHOWN,
  WINNER_BLAST_VIDEO_PAUSED,
  WINNER_BLAST_VIDEO_PLAYED
} from '~/plugins/mixpanel/events'
import {
  WINNER_BLAST_REACTION,
  WINNER_BLAST_VIDEO,
  WINNER_BLAST_VIDEO_CURRENT_TIME,
  WINNER_BLAST_WINNER
} from '~/plugins/mixpanel/props'
import { Reaction } from '~/store/persistent'
import { Winner } from '~/types/jsonapi/winner'

const reactionNames = {
  '👏': 'clap',
  '🎉': 'confetti',
  '❤️': 'heart',
  '👀': 'eyes',
  '🔥': 'fire'
}

@Component({
  name: 'WinnerModal',
  components: {
    Icon,
    ReadMore,
    VideoPlayer,
    VueLottiePlayer
  }
})
export default class WinnerModal extends Mixins(WinnerBlastFeature) {
  winner: Winner = null
  isNewWinner = false
  hasUserClickedReaction = false

  get formatToUSDCurrency() {
    return formatToUSDCurrency
  }

  get reactions(): Reaction[] {
    return ['👏', '🎉', '❤️', '👀', '🔥']
  }

  get recordedReaction(): Reaction {
    return this.$accessor.persistent.winnerBlastReactions[this.winner?.id]
  }

  set recordedReaction(newReaction) {
    const { id } = this.winner ?? {}
    const reaction = newReaction === this.recordedReaction ? null : newReaction
    this.$accessor.persistent.saveReaction({ id, reaction })
  }

  get dismissed() {
    return this.$accessor.persistent.winnerBlastDismissed
  }

  set dismissed(data) {
    this.$accessor.persistent.setState({
      stateName: 'winnerBlastDismissed',
      data
    })
  }

  get isWinnerSeenAlready() {
    return this.winner
      ? this.winner.id === this.$accessor.persistent.winnerBlastLastSeenWinner
      : false
  }

  isShown() {
    return Boolean(
      this.getWinnerBlastFeatureVisible() &&
        this.winner &&
        (!this.isWinnerSeenAlready || !this.dismissed)
    )
  }

  updateLastSeenWinner() {
    const { id } = this.winner ?? {}
    if (id) {
      this.$accessor.persistent.setState({
        stateName: 'winnerBlastLastSeenWinner',
        data: id
      })
    }
  }

  async created() {
    try {
      const isDebugMode = this.$featureFlags?.isEnabled?.(DEBUG_MODE)
      const id = this.$route.query?.debugWinnerBlastById
      let response

      switch (true) {
        case isDebugMode && Boolean(id):
          response = await this.$api.winners.byId({ id })
          // eslint-disable-next-line no-console
          console.warn(
            'Showing winner blast with manually specified winner ID',
            id
          )
          break

        case Boolean(id):
          // eslint-disable-next-line no-console
          console.warn(
            'Attempted to show winner blast with manually specified winner ID',
            id,
            'but this user does not have debug mode enabled by Unleash'
          )

        // eslint-disable-next-line no-fallthrough
        default:
          response = await this.$api.winners.latest()
      }

      setTimeout(() => {
        this.winner = deserialize<Collection<Winner>>(response.rawRes)?.[0]

        this.$mixpanel.track('$experiment_started', {
          'Experiment name': 'Dashboard Winner Blast',
          'Variant name': this.getWinnerBlastFeatureVariant()
        })

        if (this.getWinnerBlastFeatureVisible()) {
          this.$mixpanel.track(WINNER_BLAST_SHOWN, {
            [WINNER_BLAST_WINNER]: this.winner?.id,
            [WINNER_BLAST_VIDEO]: Boolean(this.winner?.testimonialVideo)
          })
        }
      }, 90000)
    } catch (err) {
      this.$sentry.captureException(err)
      return false
    }
  }

  handleReact(reaction: Reaction) {
    this.recordedReaction = reaction
    this.hasUserClickedReaction = true
    this.$mixpanel.track(WINNER_BLAST_REACTED, {
      [WINNER_BLAST_REACTION]: reactionNames[reaction],
      [WINNER_BLAST_WINNER]: this.winner?.id,
      [WINNER_BLAST_VIDEO]: Boolean(this.winner?.testimonialVideo)
    })
  }

  handleDismiss() {
    this.dismissed = true
    this.updateLastSeenWinner()
    this.$mixpanel.track(WINNER_BLAST_DISMISSED, {
      [WINNER_BLAST_WINNER]: this.winner?.id,
      [WINNER_BLAST_VIDEO]: Boolean(this.winner?.testimonialVideo)
    })
  }

  trackVideoStart(currentTime: number) {
    this.$mixpanel.track(WINNER_BLAST_VIDEO_PLAYED, {
      [WINNER_BLAST_WINNER]: this.winner?.id,
      [WINNER_BLAST_VIDEO_CURRENT_TIME]: currentTime
    })
  }

  trackVideoStop(currentTime: number) {
    this.$mixpanel.track(WINNER_BLAST_VIDEO_PAUSED, {
      [WINNER_BLAST_WINNER]: this.winner?.id,
      [WINNER_BLAST_VIDEO_CURRENT_TIME]: currentTime
    })
  }
}
